import { extractLookupIds } from './lookups';

/**
 * The guided troubleshooting walk-through (KPR-491): the pure part.
 *
 * A diagnostic guide is a tree of items the Knowledge catalog's Troubleshooting
 * add-on brings: a Symptom points at its first step (`start-at`); a Check asks
 * one question and points at the next step for Yes (`if-yes`), No (`if-no`) and
 * Not sure (`if-unsure`); a Fix gives steps and may point at `if-not-fixed`; a
 * Get help ends the tree. A lookup can point at any of the three, so a tree is
 * only as sound as the people who wrote it: a branch may be blank, and two
 * branches can lead back to a step already walked.
 *
 * The walk is a PATH: a list of `{ id, answer }`, one per step visited, the
 * last one the step on screen. `answer` is what was chosen at that step, or
 * null while the step is being read. What follows from an answer (the next
 * step, or a plain ending) is always derived from the path, never stored, so
 * Back only has to clear an answer or drop a step and nothing is ever lost.
 */

export const SYMPTOM_CARD_KEY = 'symptom';

export const STEP_KIND = Object.freeze({
	CHECK: 'check',
	FIX: 'fix',
	HELP: 'get-help',
	OTHER: 'other',
});

/** The most steps one walk goes through, so a tree with a long cycle of fresh nodes still stops. */
export const MAX_STEPS = 60;

export const CHECK_ANSWERS = Object.freeze([
	{ value: 'yes', label: 'Yes', element: 'if-yes' },
	{ value: 'no', label: 'No', element: 'if-no' },
	{ value: 'unsure', label: 'Not sure', element: 'if-unsure' },
]);

/** The answer on a Fix that says it worked. The other, 'not-fixed', follows `if-not-fixed`. */
export const FIXED = 'fixed';
export const NOT_FIXED = 'not-fixed';

const ELEMENT_FOR_ANSWER = Object.freeze({
	yes: 'if-yes',
	no: 'if-no',
	unsure: 'if-unsure',
	[NOT_FIXED]: 'if-not-fixed',
});

/** Is this item a Symptom, the card that offers Start? */
export const isSymptom = (card) => card?.key === SYMPTOM_CARD_KEY;

/**
 * What kind of step an item is: by its card's key, else (a card renamed or
 * re-keyed) by an element only that kind has.
 */
export function stepKind(item, card) {
	const key = card?.key;
	if (key === 'check' || key === 'fix' || key === 'get-help') return key;
	const elements = item?.elements || {};
	if ('if-yes' in elements || 'if-no' in elements || 'how-to-check' in elements) return STEP_KIND.CHECK;
	if ('do-this' in elements || 'if-not-fixed' in elements || 'fixed-it' in elements) return STEP_KIND.FIX;
	if ('have-ready' in elements || 'urgency' in elements || 'who' in elements) return STEP_KIND.HELP;
	return STEP_KIND.OTHER;
}

/** The first id a lookup element of this item points at, or null. */
export function lookupId(item, element) {
	return extractLookupIds(item?.elements?.[element])[0] || null;
}

const text = (value) => (typeof value === 'string' ? value.trim() : '');

/** A step's own heading: a Check's question, a Fix's fix, a Get help's who. */
export function stepTitle(item, kind) {
	const e = item?.elements || {};
	const own = kind === STEP_KIND.CHECK ? e.question
		: kind === STEP_KIND.FIX ? e.fix
			: kind === STEP_KIND.HELP ? e.who
				: null;
	return text(own) || text(e.title) || text(e.question) || text(e.fix) || text(e.who) || 'This step';
}

const UNSET = Object.freeze({
	yes: 'This check does not say what to do when the answer is yes. Go back and try another answer, or ask whoever keeps this guide to set the next step.',
	no: 'This check does not say what to do when the answer is no. Go back and try another answer, or ask whoever keeps this guide to set the next step.',
	unsure: 'This check does not say what to do when you are not sure. Go back and answer yes or no, or ask whoever keeps this guide to set the next step.',
	[NOT_FIXED]: 'This fix does not say what to try next. Go back, or ask whoever keeps this guide.',
});

export const LOOP_MESSAGE = 'This path leads back to a step you already went through, so it stops here. Go back and try another answer.';
export const TOO_LONG_MESSAGE = 'This path has more steps than a walk-through follows, so it stops here. Go back and try another answer.';
export const MISSING_MESSAGE = 'This step is not available. It may have been deleted, or you may not be able to see it. Go back and try another answer.';
export const NO_START_MESSAGE = 'This symptom does not say where to start yet. Edit it and choose its first check.';

/**
 * What follows from `answer` given at `node` with `path` already walked:
 * `{ next: id }` to go on, or `{ end: message }` for a plain ending.
 */
export function resolveNext(node, answer, path) {
	const element = ELEMENT_FOR_ANSWER[answer];
	if (!element) return { end: UNSET.no };
	const id = lookupId(node, element);
	if (!id) return { end: UNSET[answer] };
	if ((path || []).some(step => step.id === id)) return { end: LOOP_MESSAGE };
	if ((path || []).length >= MAX_STEPS) return { end: TOO_LONG_MESSAGE };
	return { next: id };
}

/** A new walk, on the symptom's `start-at`. Null when it has none. */
export function startPath(symptom) {
	const id = lookupId(symptom, 'start-at');
	return id ? [{ id, answer: null }] : [];
}

/** Record `answer` on the step on screen. */
export function answerStep(path, answer) {
	if (!path.length) return path;
	return path.map((step, index) => (index === path.length - 1 ? { ...step, answer } : step));
}

/** Record `answer` and move to `nextId`. */
export function advancePath(path, answer, nextId) {
	return [...answerStep(path, answer), { id: nextId, answer: null }];
}

/** Can Back do anything: there is an answer to undo, or an earlier step. */
export const canGoBack = (path) => path.length > 1 || (path.length === 1 && path[0].answer !== null);

/**
 * One step back. An ending (the last step answered with nowhere to go) is
 * undone first, which returns to the step that gave it; then the last step
 * is dropped and the one before it is open again with its answer cleared.
 */
export function backPath(path) {
	if (!path.length) return path;
	const last = path[path.length - 1];
	if (last.answer !== null) return answerStep(path, null);
	if (path.length === 1) return path;
	return answerStep(path.slice(0, -1), null);
}

/**
 * Where the walk stands, given what has been fetched. `nodes` maps an id to the
 * item (or null when it could not be read). Returns
 * `{ phase: 'step', id }` when a step is on screen and unanswered,
 * `{ phase: 'fixed' }` once a Fix worked, or `{ phase: 'end', message }`.
 */
export function walkState(path, nodes) {
	if (!path.length) return { phase: 'end', message: NO_START_MESSAGE };
	const last = path[path.length - 1];
	if (nodes[last.id] === null) return { phase: 'end', message: MISSING_MESSAGE };
	if (last.answer === null) return { phase: 'step', id: last.id };
	if (last.answer === FIXED) return { phase: 'fixed' };
	// An answer with a next step is advanced at the click (advancePath), so a
	// path that rests on an answer is one that ended.
	const resolved = nodes[last.id] ? resolveNext(nodes[last.id], last.answer, path) : { end: MISSING_MESSAGE };
	return { phase: 'end', message: resolved.end || MISSING_MESSAGE };
}
